import styled from '@emotion/styled';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { formatSIWithUnit } from '@racemap/utilities/formattings/units';
import type { PredictionParams } from '@racemap/utilities/types/events';
import type {
  PredictionDebugData,
  PredictionDebugEventData,
} from '@racemap/utilities/types/trackping';
import { Unit } from '@racemap/utilities/types/units';
import { Col, Modal, Row } from 'antd';
import { type FC, useState } from 'react';
import { IconRunner } from 'src/components/BasicComponents/Icons';
import { PopoverHint } from '../BasicComponents/PopoverHint';
import { FormInput } from '../FormComponents';
import { PredictionStarterAnalyseLink } from './AnalyseComponents/PredictionStarterAnalyseLink';

interface Props {
  eventId: string;
  predictionParams: PredictionParams;
  starterDebugData?: PredictionDebugData | null;
  onPredictionParamsChange: (newParams: PredictionParams) => void;
}

const trackpingAPIClient = RacemapAPIClient.fromWindowLocation();

export const PredictionParamsOverride: FC<Props> = ({
  eventId,
  starterDebugData,
  predictionParams,
  onPredictionParamsChange,
}) => {
  const [_predictionParams, setPredictiveParams] = useState<PredictionParams>(predictionParams);
  const [eventPredictionErrors, setEventPredictionErrors] =
    useState<PredictionDebugEventData | null>(null);

  const handleValueChange = (details: { value: string; name: string }) => {
    const { name, value } = details;
    try {
      const asNumber = Number.parseFloat(value);
      setPredictiveParams((prev) => {
        const newPredictionParams = { ...prev, [name]: asNumber };
        onPredictionParamsChange(newPredictionParams);
        updateEventError(newPredictionParams);
        return newPredictionParams;
      });
    } catch (e) {
      console.error(`Invalid number "${value}" for field ${name} `, e);
    }
  };

  const updateEventError = async (predictionParamsOverride: PredictionParams) => {
    try {
      setEventPredictionErrors(
        await trackpingAPIClient
          .prediction()
          .getDebugDataByEvent(eventId, predictionParamsOverride),
      );
    } catch (e) {
      console.error('Exception in updateEventError', e);
    }
  };

  return (
    <OuterBox>
      <Row>
        <Col md={6}>
          <FormInput
            id="racemap_event_predictive_jumping_dots_threshold_override"
            type="number"
            label={
              <>
                Jumping dot threshold in seconds
                <PopoverHint>
                  With a new read, there is a new speed calculated for a predicted virtual
                  participant (represented by location of dot in tracking map). The "Jumping dot
                  threshold" is the maximum time difference between the latest read and the virtual
                  participant. If this difference is greater, the virtual participant jumps to the
                  location of the latest read. If this difference is smaller, the virtual
                  participant will either move faster or slower to smoothly reduce the prediction
                  error during the ongoing event. A value of <strong>0.0</strong> disables the
                  feature - no jumps will be done.
                  <br />
                  <strong>Suggested Value: 360&thinsp;s</strong>
                </PopoverHint>
              </>
            }
            name="jumpingDotThreshold"
            value={_predictionParams?.jumpingDotThreshold?.toString() || ''}
            onDetailsChange={handleValueChange}
            step="60"
          />
          <OriginalValue value={predictionParams.jumpingDotThreshold} />
        </Col>
        <Col md={6}>
          <FormInput
            id="racemap_event_predictive_distance_to_track_override"
            type="number"
            label={
              <>
                Max distance from track in meters
                <PopoverHint>
                  Maximum distance between shadowtrack and reader in which reads can be considered
                  for prediction. Readers with a greater distance from the shadowtrack are not
                  considered for prediction and are shown as "Out of range". If the distance is
                  50&thinsp;m the width of the whole corridor is 100&thinsp;m.
                  <br />
                  <strong>Suggested Value: 50&thinsp;m</strong>
                </PopoverHint>
              </>
            }
            name="distanceFromTrack"
            value={_predictionParams?.distanceFromTrack?.toString() || ''}
            onDetailsChange={handleValueChange}
            step="1.0"
          />
          <OriginalValue value={predictionParams.distanceFromTrack} />
        </Col>
        <Col md={6}>
          <FormInput
            id="racemap_event_predictive_distance_to_track_override"
            type="number"
            label={
              <>
                RSSI Threshold in dBm
                <PopoverHint>
                  With Track Boxes the reads have a Received Signal Strength Indicator (RSSI). The
                  RSSI threshold is the minimal value to be considered for prediction. Reads with a
                  smaller RSSI are not considered for prediction, as small values indicate a
                  significant distance between Track Box and transponder. Allowing small RSSI values
                  increases possible errors as the transponder could be detected by multiple Track
                  Boxes over a long distance at nearly the same time.
                  <br />
                  <strong>Suggested Value: -75</strong>
                </PopoverHint>
              </>
            }
            name="rssiThreshold"
            value={_predictionParams?.rssiThreshold?.toString() || ''}
            onDetailsChange={handleValueChange}
            step="1.0"
          />
          <OriginalValue value={predictionParams.rssiThreshold} />
        </Col>
        <Col md={6}>
          <StarterErrorBox>
            <IconRunner
              style={{ marginTop: '4px', marginRight: '5px' }}
              title="Average prediction error for this starter in meters."
              color={RacemapColors.PaleBlue}
            />{' '}
            Error: {formatSIWithUnit(starterDebugData?.predictionError, Unit.Distance.meter)}
            <MoveTopRight>
              <PopoverHint>
                The prediction error per starter in meters is considered to be an average error. For
                example if you could beam to the real world track where racemap shows the live
                location of the starter in no time and the prediction error is lets say 58 m. The
                real starter would either be 58 m in front or 58 m behind the racemap live location.
                <br />
                <strong>Smaller is better!</strong>
              </PopoverHint>
            </MoveTopRight>
          </StarterErrorBox>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormInput
            id="racemap_event_predictive_future_track_scale_value_override"
            type="number"
            label={
              <>
                Future track scale value
                <PopoverHint>
                  The relative distance in which the predicted virtual participant "meets" the
                  participant moving along with the measured average speed. The greater the track
                  scale value the longer takes the correction.
                  <br />
                  <strong>Suggested Value: 1.5</strong>
                </PopoverHint>
              </>
            }
            name="futureTrackScaleValue"
            value={_predictionParams?.futureTrackScaleValue?.toString() || ''}
            onDetailsChange={handleValueChange}
            step="0.1"
          />
          <OriginalValue value={predictionParams.futureTrackScaleValue} />
        </Col>
        <Col md={6}>
          <FormInput
            id="racemap_event_predictive_future_track_scale_value_override"
            type="number"
            label={
              <>
                Look back distance in m
                <PopoverHint>
                  The look back distance makes prediction less susceptible to measurement errors.
                  With a new read, there is a new speed calculated for a predicted virtual
                  participant. The prediction might receive many reads from a short section of the
                  track. These reads can have location errors and therefore cause malicious
                  velocities. The look back distance is that length for calculating an average speed
                  value for the prediction.
                  <br />
                  <strong>Suggested Value: 1000&thinsp;m</strong>
                </PopoverHint>
              </>
            }
            name="lookBackDistance"
            value={_predictionParams?.lookBackDistance?.toString() || ''}
            onDetailsChange={handleValueChange}
            step="0.1"
          />
          <OriginalValue value={predictionParams.lookBackDistance} />
        </Col>
        <Col md={6}>
          <FormInput
            id="racemap_event_predictive_jumping_dots_threshold_override"
            type="number"
            label={
              <>
                Look Forward Time in seconds
                <PopoverHint>
                  The look forward time compensates for the delay in data transmission and
                  processing throughout our systems.
                  <br />
                  <strong>Suggested Value: 15&thinsp;s</strong>
                </PopoverHint>
              </>
            }
            name="lookForwardTime"
            value={_predictionParams?.lookForwardTime?.toString() || ''}
            onDetailsChange={handleValueChange}
            step="60"
          />
          <OriginalValue value={predictionParams.lookForwardTime} />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <StarterErrorBox>
            <IconRunner
              style={{ marginTop: '4px', marginRight: '5px' }}
              title="Average prediction error for this starter in meters."
              color={RacemapColors.PaleBlue}
            />{' '}
            Min:{' '}
            {eventPredictionErrors?.predictionErrorMax.error ? (
              <>
                <span style={{ marginLeft: '5px' }}>
                  {formatSIWithUnit(
                    eventPredictionErrors?.predictionErrorMin.error,
                    Unit.Distance.m,
                  )}
                </span>
                <PredictionStarterAnalyseLink
                  eventId={eventId}
                  starterId={eventPredictionErrors?.predictionErrorMin.starterId || ''}
                />
              </>
            ) : (
              ' - '
            )}
            <MoveTopRight>
              <PopoverHint>
                This is the minimum prediction error for this event in meters. This starter we
                predicted best.
                <br />
                <strong>Smaller is better!</strong>
              </PopoverHint>
            </MoveTopRight>
          </StarterErrorBox>
        </Col>
        <Col md={6}>
          <EventErrorBox>
            <IconRunner style={{ marginTop: '4px' }} color={RacemapColors.PaleBlue} />
            <IconRunner
              style={{ marginTop: '4px', marginRight: '5px' }}
              title="Average prediction error over all starters in meters."
              color={RacemapColors.PaleBlue}
            />{' '}
            Error: {formatSIWithUnit(eventPredictionErrors?.predictionError, Unit.Distance.m)}
            <MoveTopRight>
              <PopoverHint>
                The prediction error over all starters in meters is the aritmetic mean value over
                all starter prediction errors.
                <br />
                <strong>Smaller is better!</strong>
              </PopoverHint>
            </MoveTopRight>
          </EventErrorBox>
        </Col>
        <Col md={6}>
          {' '}
          <StarterErrorBox>
            <IconRunner
              style={{ marginTop: '4px', marginRight: '5px' }}
              title="Average prediction error for this starter in meters."
              color={RacemapColors.PaleBlue}
            />{' '}
            Max:{' '}
            {eventPredictionErrors?.predictionErrorMax.error ? (
              <>
                <span style={{ marginLeft: '5px' }}>
                  {formatSIWithUnit(
                    eventPredictionErrors?.predictionErrorMax.error,
                    Unit.Distance.m,
                  )}
                </span>
                <PredictionStarterAnalyseLink
                  eventId={eventId}
                  starterId={eventPredictionErrors?.predictionErrorMax.starterId || ''}
                />
              </>
            ) : (
              ' - '
            )}
            <MoveTopRight>
              <PopoverHint>
                This is the maximum prediction error for this event in meters. This starter we
                predicted worst.
                <br />
                <strong>Smaller is better!</strong>
              </PopoverHint>
            </MoveTopRight>
          </StarterErrorBox>
        </Col>
        <Col md={6}>
          <ImageThumbnail
            title={'All starters over offset'}
            imageData={eventPredictionErrors?.cloudPlotOverOffset}
          />
          <ImageThumbnail
            title={'All starters over time'}
            imageData={eventPredictionErrors?.cloudPlotOverTime}
          />
        </Col>
      </Row>
    </OuterBox>
  );
};

const OriginalValue = ({ value }: { value: number | string | null }) => {
  return <OriginalContainer>Event setting: {value ? value.toString() : ''}</OriginalContainer>;
};

const ImageThumbnail = ({ title, imageData }: { title: string; imageData?: string }) => {
  const [visible, setVisible] = useState(false);

  if (!imageData) {
    return <p>Loading...</p>;
  }

  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <img
        src={`data:image/png;base64,${imageData}`}
        title={`${title} - Click to enlarge`}
        alt="Thumbnail"
        style={{
          height: 50,
          objectFit: 'cover',
          cursor: 'pointer',
          borderRadius: 4,
          marginTop: 4,
          border: '1px solid #ddd',
        }}
        onClick={() => setVisible(true)}
      />

      <Modal width="70vw" open={visible} footer={null} onCancel={() => setVisible(false)} centered>
        <h3>{title}</h3>
        <img
          src={`data:image/png;base64,${imageData}`}
          alt="Full details"
          style={{ width: '100%', borderRadius: 8 }}
        />
      </Modal>
    </>
  );
};

const MoveTopRight = styled.span` 
  margin-left: 6px;    
  position: relative;
  top: -5px;    
  transform: scale(0.8);
  transform-origin: top right;  
  display: inline-block;
`;

const OuterBox = styled.div`
  margin-top: 15px;
`;

const StarterErrorBox = styled.div`
  display: flex;
  margin-top: 5px;
  padding: 10px;
  border: 1px solid #f0f0f0;
  border-radius: 5px;
  background-color: #f8f8f8;
  font-size: 130%;
 `;

const EventErrorBox = styled.div`
  display: flex;
  margin-top: 5px;
  padding: 10px;
  border: 1px solid #f0f0f0;
  border-radius: 5px;
  background-color: #f8f8f8;
  font-size: 130%;
 `;

const OriginalContainer = styled.div`
  margin-top: -13px;
  font-size: 10px;
`;
